import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import ClientAnamnesisOffcanvas from "../../../../Components/Studio/ClientAnamnesisOffcanvas";
import DashboardCustomizeOffcanvas from "../../../../Components/Studio/Dashboard/DashboardCustomizeOffcanvas";
import DashboardDayEstimateCard from "../../../../Components/Studio/Dashboard/DashboardDayEstimateCard";
import DashboardHomeHeader from "../../../../Components/Studio/Dashboard/DashboardHomeHeader";
import DashboardInsightsPanel from "../../../../Components/Studio/Dashboard/DashboardInsightsPanel";
import DashboardPendingsCard from "../../../../Components/Studio/Dashboard/DashboardPendingsCard";
import DayCloseReminderBanner from "../../../../Components/Studio/Financial/DayCloseReminderBanner";
import ActivationHintBanner from "../../../../Components/Studio/Onboarding/ActivationHintBanner";
import ConciergeDeepLinkHost from "../../../../Components/Studio/Onboarding/ConciergeDeepLinkHost";
import FirstExperienceModal from "../../../../Components/Studio/Onboarding/FirstExperienceModal";
import WelcomePlayStoreBanner from "../../../../Components/Studio/WelcomePlayStoreBanner";
import TrialExpiryBanner from "../../../../Components/Studio/Plan/TrialExpiryBanner";
import {
  isLateActiveAppointment,
  isLateArrivableAppointment,
  playArrivedChime,
} from "../../../../helpers/arrivedFeedback";
import {
  resolveCurrentTenantId,
  setAgendaListLateOnlyForTenant,
  setAgendaTabForTenant,
} from "../../../../helpers/agendaUnitContext";
import { canViewClinicalRecords } from "../../../../helpers/clinicalRecordAccess";
import {
  buildHealthRecordPendings,
  sumAppointmentsEstimatedCents,
} from "../../../../helpers/dashboardDayMetrics";
import {
  appointmentToBatchHealthQueueItem,
  promptContinueBatchHealthQueue,
  type BatchHealthQueueItem,
} from "../../../../helpers/promptBatchHealthRecordQueue";
import {
  DashboardWidgetId,
  DashboardWidgetLayoutItem,
  canCustomizeDashboard,
  eligibleWidgetIds,
  groupVisibleWidgets,
  hasVisibleKpiWidgets,
  isKpiWidget,
  mergeLayoutPreference,
} from "../../../../helpers/dashboardLayoutCatalog";
import { formatMoneyFromCents } from "../../../../helpers/money";
import {
  appointmentNeedsHealthRecord,
  promptFillHealthRecordIfMissing,
} from "../../../../helpers/promptFillHealthRecord";
import { canMarkClientArrived } from "../../../../Routes/roleAccess";
import {
  Appointment,
  clearClientArrived,
  getAll as getAppointments,
  markClientArrived,
} from "../../../../services/appointmentService";
import { getAuthRoleNames, userHasAnyRole } from "../../../../services/authService";
import { getClients } from "../../../../services/clientService";
import { getDashboardLayout } from "../../../../services/dashboardLayoutService";
import { getDayCloseReport } from "../../../../services/dayCloseReportService";
import { getAll as getOrders } from "../../../../services/orderService";
import { getProfessionals } from "../../../../services/professionalService";
import { getExpiringCreditsSummary } from "../../../../services/servicePackageService";
import {
  SupportTicket,
  getRecentImprovements,
} from "../../../../services/supportTicketService";

interface DashboardStats {
  clients: number;
  professionals: number;
  appointmentsToday: number;
  ordersOpen: number;
  packagesExpiring: number;
}

const pad2 = (value: number): string => String(value).padStart(2, "0");

const formatTimeLabel = (datetime: string): string => datetime.slice(11, 16);

const COUNT_QUERY = { page: 1, per_page: 1 } as const;
const NOT_AVAILABLE = "—";

const StudioDashboard = () => {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  }, []);

  const roles = useMemo(() => getAuthRoleNames(), []);
  const allowCustomize = useMemo(() => canCustomizeDashboard(roles), [roles]);
  const allowMarkArrived = useMemo(() => canMarkClientArrived(), []);
  const canHealthRecord = useMemo(() => canViewClinicalRecords(), []);
  const canSeeSupport = userHasAnyRole(["owner", "manager"]);
  const isSdr = userHasAnyRole(["sdr"]);
  const isOwner = userHasAnyRole(["owner"]);
  const [holdAutoPrompts, setHoldAutoPrompts] = useState(isOwner);

  const [committedLayout, setCommittedLayout] = useState<
    DashboardWidgetLayoutItem[]
  >(() => mergeLayoutPreference(null, roles));
  const [previewLayout, setPreviewLayout] = useState<
    DashboardWidgetLayoutItem[] | null
  >(null);
  const layoutWidgets = previewLayout ?? committedLayout;
  const [layoutReady, setLayoutReady] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const showKpiEmpty = useMemo(() => {
    const eligibleKpis = eligibleWidgetIds(roles).filter(isKpiWidget);
    if (eligibleKpis.length === 0 || hasVisibleKpiWidgets(layoutWidgets)) {
      return false;
    }
    // Agenda-first default hides KPIs on purpose — don't nag while ops widgets are on.
    const hasOpsWidgets = layoutWidgets.some(
      (widget) =>
        widget.visible
        && (
          widget.id === "appointments_today"
          || widget.id === "day_estimate"
          || widget.id === "pendings"
          || widget.id === "insights"
        )
    );
    return !hasOpsWidgets;
  }, [layoutWidgets, roles]);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    clients: 0,
    professionals: 0,
    appointmentsToday: 0,
    ordersOpen: 0,
    packagesExpiring: 0,
  });
  const [packagesForbidden, setPackagesForbidden] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [paidTodayCents, setPaidTodayCents] = useState(0);
  const [recentImprovements, setRecentImprovements] = useState<SupportTicket[]>([]);
  const [arrivingId, setArrivingId] = useState<number | null>(null);
  const [batchArriving, setBatchArriving] = useState(false);
  const [anamnesisTarget, setAnamnesisTarget] = useState<{
    id: number;
    name: string;
    serviceId?: number | null;
    initialTemplateId?: number | null;
  } | null>(null);
  const batchHealthQueueRef = useRef<BatchHealthQueueItem[]>([]);

  const visibleIds = useMemo(() => {
    const set = new Set<DashboardWidgetId>();
    layoutWidgets.forEach((item) => {
      if (item.visible) {
        set.add(item.id);
      }
    });
    return set;
  }, [layoutWidgets]);

  const renderGroups = useMemo(
    () => groupVisibleWidgets(layoutWidgets),
    [layoutWidgets]
  );

  const lateArrivableAppointments = useMemo(() => {
    if (!allowMarkArrived) {
      return [] as Appointment[];
    }
    const nowMs = Date.now();
    return appointments.filter((appointment) =>
      isLateArrivableAppointment(appointment, nowMs)
    );
  }, [allowMarkArrived, appointments]);

  const lateActiveAppointmentsCount = useMemo(() => {
    const nowMs = Date.now();
    return appointments.filter((appointment) =>
      isLateActiveAppointment(appointment, nowMs)
    ).length;
  }, [appointments]);

  const estimatedCents = useMemo(
    () => sumAppointmentsEstimatedCents(appointments),
    [appointments]
  );

  const pendingItems = useMemo(
    () => buildHealthRecordPendings(appointments, today),
    [appointments, today]
  );

  const loadLayout = useCallback(async () => {
    try {
      const saved = await getDashboardLayout();
      setCommittedLayout(mergeLayoutPreference(saved?.widgets ?? null, roles));
    } catch {
      setCommittedLayout(mergeLayoutPreference(null, roles));
    } finally {
      setLayoutReady(true);
    }
  }, [roles]);

  useEffect(() => {
    void loadLayout();
  }, [loadLayout]);

  useEffect(() => {
    const onFocus = () => {
      if (customizeOpen) {
        return;
      }
      void loadLayout();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        onFocus();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [customizeOpen, loadLayout]);

  const loadDashboard = useCallback(async () => {
    if (!layoutReady) {
      return;
    }

    setLoading(true);

    const needClients = visibleIds.has("kpi_clients");
    const needProfessionals = visibleIds.has("kpi_professionals");
    const needAppointmentsCount = visibleIds.has("kpi_appointments_today");
    const needOrders = visibleIds.has("kpi_orders_open");
    const needPackages = visibleIds.has("kpi_packages_expiring");
    const needAppointmentsList =
      visibleIds.has("appointments_today")
      || visibleIds.has("day_estimate")
      || visibleIds.has("pendings");
    const needDayPaid =
      visibleIds.has("day_estimate") && userHasAnyRole(["owner", "manager"]);
    const needImprovements =
      visibleIds.has("recent_improvements") && canSeeSupport;

    try {
      const [
        clientsResponse,
        professionalsResponse,
        appointmentsCountResponse,
        ordersOpenResponse,
        appointmentsListResponse,
        expiringCreditsResponse,
        improvementsResponse,
        dayCloseResponse,
      ] = await Promise.all([
        needClients ? getClients(COUNT_QUERY) : Promise.resolve(null),
        needProfessionals ? getProfessionals(COUNT_QUERY) : Promise.resolve(null),
        needAppointmentsCount
          ? getAppointments({ ...COUNT_QUERY, date: today })
          : Promise.resolve(null),
        needOrders
          ? getOrders({ ...COUNT_QUERY, status: "open" })
          : Promise.resolve(null),
        needAppointmentsList
          ? getAppointments({ page: 1, per_page: 50, date: today })
          : Promise.resolve(null),
        needPackages
          ? getExpiringCreditsSummary(30).catch((error: unknown) => {
              const code = (error as { code?: string })?.code;
              if (code === "FORBIDDEN") {
                return { __forbidden: true } as const;
              }
              return null;
            })
          : Promise.resolve(null),
        needImprovements
          ? getRecentImprovements(5).catch(() => null)
          : Promise.resolve(null),
        needDayPaid
          ? getDayCloseReport({ date: today, consolidate: true }).catch(() => null)
          : Promise.resolve(null),
      ]);

      const packagesForbiddenResult =
        expiringCreditsResponse !== null
        && typeof expiringCreditsResponse === "object"
        && "__forbidden" in expiringCreditsResponse;

      setPackagesForbidden(Boolean(packagesForbiddenResult));

      setStats({
        clients: clientsResponse?.meta.total ?? 0,
        professionals: professionalsResponse?.meta.total ?? 0,
        appointmentsToday: appointmentsCountResponse?.meta.total ?? 0,
        ordersOpen: ordersOpenResponse?.meta.total ?? 0,
        packagesExpiring: packagesForbiddenResult
          ? 0
          : (expiringCreditsResponse as { data?: { lots_expiring?: number } } | null)
              ?.data?.lots_expiring ?? 0,
      });
      setAppointments(appointmentsListResponse?.data ?? []);
      setPaidTodayCents(dayCloseResponse?.data?.totals?.gross_cents ?? 0);
      setRecentImprovements(
        improvementsResponse?.success ? improvementsResponse.data || [] : []
      );
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError?.data || t("studio.dashboard.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [canSeeSupport, layoutReady, t, today, visibleIds]);

  useEffect(() => {
    document.title = t("studio.dashboard.pageTitle");
  }, [t]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const openHealthRecord = useCallback((appointment: Appointment) => {
    if (!appointment.client_id) {
      return;
    }
    setAnamnesisTarget({
      id: appointment.client_id,
      name: appointment.client?.name ?? NOT_AVAILABLE,
      serviceId: appointment.service_id,
      initialTemplateId: appointment.service?.client_record_template_id ?? null,
    });
  }, []);

  const openHealthRecordFromQueueItem = useCallback(
    (item: BatchHealthQueueItem) => {
      setAnamnesisTarget({
        id: item.clientId,
        name: item.name,
        serviceId: item.serviceId ?? null,
        initialTemplateId: item.initialTemplateId ?? null,
      });
    },
    []
  );

  const bumpClientHealthRecordsCount = useCallback((clientId: number) => {
    setAppointments((current) =>
      current.map((item) => {
        if (item.client_id !== clientId || !item.client) {
          return item;
        }
        return {
          ...item,
          needs_health_record: false,
          client: {
            ...item.client,
            health_records_count: (item.client.health_records_count ?? 0) + 1,
          },
        };
      })
    );
  }, []);

  const handleAnamnesisRecordSaved = useCallback(
    (clientId: number) => {
      bumpClientHealthRecordsCount(clientId);

      const remaining = batchHealthQueueRef.current.filter(
        (item) => item.clientId !== clientId
      );
      batchHealthQueueRef.current = remaining;

      if (remaining.length === 0) {
        return;
      }

      void (async () => {
        const next = remaining[0];
        const decision = await promptContinueBatchHealthQueue(
          next,
          remaining.length,
          t
        );

        if (decision !== "fill") {
          batchHealthQueueRef.current = [];
          return;
        }

        batchHealthQueueRef.current = remaining.slice(1);
        openHealthRecordFromQueueItem(next);
      })();
    },
    [bumpClientHealthRecordsCount, openHealthRecordFromQueueItem, t]
  );

  const openLateListFromBadge = useCallback(() => {
    const tenantId = resolveCurrentTenantId();
    setAgendaListLateOnlyForTenant(tenantId, true);
    setAgendaTabForTenant(tenantId, "list");
  }, []);

  const handleClearArrived = useCallback(
    async (appointment: Appointment) => {
      setArrivingId(appointment.id);
      try {
        const response = await clearClientArrived(appointment.id);
        const next = response.data ?? { ...appointment, client_arrived_at: null };
        setAppointments((current) =>
          current.map((row) => (row.id === appointment.id ? next : row))
        );
        toast.info(response.message || t("studio.agenda.actions.arrived.undone"));
      } catch (error: unknown) {
        const apiError = error as { data?: string };
        toast.error(apiError?.data || t("studio.agenda.actions.arrived.undoError"));
      } finally {
        setArrivingId(null);
      }
    },
    [t]
  );

  const handleMarkArrived = useCallback(
    async (appointment: Appointment) => {
      if (
        !allowMarkArrived
        || appointment.client_arrived_at
        || !["scheduled", "confirmed"].includes(appointment.status)
      ) {
        return;
      }

      setArrivingId(appointment.id);
      try {
        const response = await markClientArrived(appointment.id);
        const next =
          response.data
          ?? ({
            ...appointment,
            client_arrived_at: new Date().toISOString(),
          } as Appointment);

        setAppointments((current) =>
          current.map((row) => (row.id === appointment.id ? next : row))
        );
        playArrivedChime();
        toast.success(
          <span>
            {response.message || t("studio.agenda.actions.arrived.success")}{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => {
                void handleClearArrived(next);
              }}
            >
              {t("studio.agenda.actions.arrived.undo")}
            </button>
          </span>,
          { closeOnClick: false, autoClose: 8000 }
        );

        void promptFillHealthRecordIfMissing({
          canView: canHealthRecord,
          appointment: next,
          notAvailable: NOT_AVAILABLE,
          t,
          onFill: openHealthRecord,
        });
      } catch (error: unknown) {
        const apiError = error as { data?: string };
        toast.error(apiError?.data || t("studio.agenda.actions.arrived.error"));
      } finally {
        setArrivingId(null);
      }
    },
    [allowMarkArrived, canHealthRecord, handleClearArrived, openHealthRecord, t]
  );

  const handleBatchMarkArrived = useCallback(async () => {
    if (!allowMarkArrived || lateArrivableAppointments.length === 0 || batchArriving) {
      return;
    }

    setBatchArriving(true);
    let ok = 0;
    const markedWithoutHealth: Appointment[] = [];

    try {
      for (const appointment of lateArrivableAppointments) {
        try {
          const response = await markClientArrived(appointment.id);
          const next =
            response.data
            ?? ({
              ...appointment,
              client_arrived_at: new Date().toISOString(),
            } as Appointment);
          setAppointments((current) =>
            current.map((row) => (row.id === appointment.id ? next : row))
          );
          ok += 1;
          if (appointmentNeedsHealthRecord(next) && next.client_id) {
            markedWithoutHealth.push(next);
          }
        } catch {
          // continue
        }
      }

      if (ok > 0) {
        playArrivedChime();
        toast.success(
          ok === 1
            ? t("studio.agenda.actions.arrived.batchSuccessOne")
            : t("studio.agenda.actions.arrived.batchSuccess", { count: ok })
        );

        if (canHealthRecord && markedWithoutHealth.length > 0) {
          const confirmation = await Swal.fire({
            title: t("studio.agenda.actions.arrived.batchMissingHealthTitle"),
            text: t("studio.agenda.actions.arrived.batchMissingHealthBody", {
              count: markedWithoutHealth.length,
            }),
            icon: "question",
            showCancelButton: true,
            confirmButtonText: t(
              "studio.agenda.actions.arrived.batchMissingHealthFill"
            ),
            cancelButtonText: t(
              "studio.agenda.actions.arrived.batchMissingHealthLater"
            ),
            confirmButtonColor: "#0ab39c",
            cancelButtonColor: "#878a99",
            reverseButtons: true,
          });

          if (confirmation.isConfirmed) {
            const queue = markedWithoutHealth
              .map((item) =>
                appointmentToBatchHealthQueueItem(item, NOT_AVAILABLE)
              )
              .filter((item): item is BatchHealthQueueItem => item !== null);
            if (queue.length > 0) {
              batchHealthQueueRef.current = queue.slice(1);
              openHealthRecordFromQueueItem(queue[0]);
            }
          } else {
            batchHealthQueueRef.current = [];
          }
        }
      } else {
        toast.error(t("studio.agenda.actions.arrived.batchError"));
      }
    } finally {
      setBatchArriving(false);
    }
  }, [
    allowMarkArrived,
    batchArriving,
    canHealthRecord,
    lateArrivableAppointments,
    openHealthRecordFromQueueItem,
    t,
  ]);

  const kpiMeta: Record<
    string,
    { label: string; value: number; icon: string; color: string; link: string }
  > = {
    kpi_clients: {
      label: t("studio.dashboard.widgets.clients"),
      value: stats.clients,
      icon: "ri-user-heart-line",
      color: "primary",
      link: "/clients",
    },
    kpi_professionals: {
      label: t("studio.dashboard.widgets.professionals"),
      value: stats.professionals,
      icon: "ri-team-line",
      color: "info",
      link: "/professionals",
    },
    kpi_appointments_today: {
      label: t("studio.dashboard.widgets.appointmentsToday"),
      value: stats.appointmentsToday,
      icon: "ri-calendar-check-line",
      color: "success",
      link: "/agenda",
    },
    kpi_orders_open: {
      label: t("studio.dashboard.widgets.ordersOpen"),
      value: stats.ordersOpen,
      icon: "ri-file-list-3-line",
      color: "warning",
      link: "/orders",
    },
    kpi_packages_expiring: {
      label: t("studio.dashboard.widgets.packagesExpiring"),
      value: stats.packagesExpiring,
      icon: "ri-timer-flash-line",
      color: "danger",
      link: "/clients?has_active_credits=1",
    },
  };

  const renderKpiCard = (id: DashboardWidgetId) => {
    if (id === "kpi_packages_expiring" && packagesForbidden) {
      return null;
    }
    const widget = kpiMeta[id];
    if (!widget) {
      return null;
    }

    return (
      <Col xl={3} md={6} key={id}>
        <Card className="card-animate">
          <CardBody>
            <div className="d-flex align-items-center">
              <div className="avatar-sm flex-shrink-0">
                <span
                  className={`avatar-title bg-${widget.color}-subtle text-${widget.color} rounded-2 fs-2`}
                >
                  <i className={widget.icon} />
                </span>
              </div>
              <div className="flex-grow-1 overflow-hidden ms-3">
                <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                  {widget.label}
                </p>
                <h4 className="fs-4 mb-2">
                  <CountUp start={0} end={widget.value} duration={1.2} />
                </h4>
                <Link to={widget.link} className="text-decoration-underline">
                  {t("studio.dashboard.widgets.view")}
                </Link>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  const renderAppointmentsCard = () => (
    <Col xl={12} key="appointments_today">
      <Card className="border-0 shadow-sm rounded-3">
        <CardHeader className="bg-transparent border-0 d-flex align-items-start justify-content-between gap-2 flex-wrap pb-2">
          <div>
            <h5 className="card-title mb-1 fw-semibold">
              {t("studio.dashboard.appointments.title")}
            </h5>
            <p className="text-muted small mb-0">
              {t("studio.dashboard.appointments.count", {
                count: appointments.length,
              })}
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {lateActiveAppointmentsCount > 0 ? (
              <Link
                to="/agenda"
                className="badge rounded-pill bg-warning-subtle text-warning text-decoration-none px-3 py-2"
                title={t("studio.dashboard.appointments.lateBadgeHint")}
                onClick={openLateListFromBadge}
              >
                <i className="ri-time-line me-1" aria-hidden="true" />
                {t("studio.dashboard.appointments.lateBadge", {
                  count: lateActiveAppointmentsCount,
                })}
              </Link>
            ) : null}
            {lateArrivableAppointments.length > 0 ? (
              <Button
                color="info"
                size="sm"
                outline
                disabled={batchArriving || arrivingId !== null}
                title={t("studio.agenda.actions.arrived.batchHint")}
                onClick={() => void handleBatchMarkArrived()}
              >
                {batchArriving ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-walk-line me-1" aria-hidden="true" />
                )}
                {t("studio.agenda.actions.arrived.batch")}
                <Badge color="light" className="ms-1 text-body">
                  {lateArrivableAppointments.length}
                </Badge>
              </Button>
            ) : null}
            <Link to="/agenda" className="btn btn-soft-success btn-sm">
              {t("studio.dashboard.appointments.openGrid")}
            </Link>
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          {appointments.length === 0 ? (
            <div className="py-4 text-center text-md-start">
              <h6 className="mb-1">{t("studio.dashboard.appointments.emptyTitle")}</h6>
              <p className="text-muted mb-3">{t("studio.dashboard.appointments.emptyBody")}</p>
              <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
                <Button tag={Link} to="/agenda?action=create" color="success" size="sm">
                  {t("studio.dashboard.home.newAppointment")}
                </Button>
                <Button tag={Link} to="/services" color="soft-success" size="sm">
                  {t("studio.dashboard.appointments.emptyAddService")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle mb-0 table-borderless">
                <thead>
                  <tr className="text-muted text-uppercase small" style={{ letterSpacing: "0.04em" }}>
                    <th className="fw-medium border-0 pb-2">{t("studio.dashboard.table.time")}</th>
                    <th className="fw-medium border-0 pb-2">{t("studio.dashboard.table.client")}</th>
                    <th className="fw-medium border-0 pb-2">{t("studio.dashboard.table.professional")}</th>
                    <th className="fw-medium border-0 pb-2">{t("studio.dashboard.table.service")}</th>
                    <th className="fw-medium border-0 pb-2 text-end">{t("studio.dashboard.table.value")}</th>
                    {allowMarkArrived ? (
                      <th className="fw-medium border-0 pb-2 text-end">
                        {t("studio.dashboard.table.actions")}
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appointment) => {
                    const canArrive =
                      allowMarkArrived
                      && ["scheduled", "confirmed"].includes(appointment.status)
                      && !appointment.client_arrived_at;
                    const showNoHealth =
                      canHealthRecord && appointmentNeedsHealthRecord(appointment);
                    const valueCents =
                      (typeof appointment.service?.price === "number"
                        ? appointment.service.price
                        : 0)
                      + (appointment.extra_services ?? []).reduce(
                        (sum, extra) =>
                          sum + (typeof extra.price === "number" ? extra.price : 0),
                        0
                      );

                    return (
                      <tr key={appointment.id} className="border-top border-light">
                        <td className="py-3 text-nowrap">{formatTimeLabel(appointment.start_datetime)}</td>
                        <td className="py-3">
                          <span className="fw-semibold">
                            {appointment.client?.name ?? NOT_AVAILABLE}
                          </span>
                          {showNoHealth ? (
                            <Badge
                              color="danger"
                              pill
                              className="ms-1 bg-danger-subtle text-danger align-middle"
                              role="button"
                              style={{ cursor: "pointer" }}
                              title={t("studio.agenda.badges.noHealthRecordHint")}
                              onClick={() => openHealthRecord(appointment)}
                            >
                              {t("studio.agenda.badges.noHealthRecord")}
                            </Badge>
                          ) : null}
                          {appointment.client_arrived_at ? (
                            <Badge color="success" className="ms-1">
                              {t("studio.agenda.badges.arrived")}
                            </Badge>
                          ) : null}
                        </td>
                        <td className="py-3">{appointment.professional?.name ?? NOT_AVAILABLE}</td>
                        <td className="py-3">{appointment.service?.name ?? NOT_AVAILABLE}</td>
                        <td className="py-3 text-end text-nowrap fw-medium">
                          {formatMoneyFromCents(valueCents, i18n.language)}
                        </td>
                        {allowMarkArrived ? (
                          <td className="py-3 text-end">
                            {canArrive ? (
                              <Button
                                color="info"
                                size="sm"
                                outline
                                disabled={arrivingId === appointment.id}
                                onClick={() => void handleMarkArrived(appointment)}
                              >
                                {arrivingId === appointment.id ? (
                                  <Spinner size="sm" className="me-1" />
                                ) : null}
                                {t("studio.agenda.actions.arrived.label")}
                              </Button>
                            ) : appointment.client_arrived_at ? (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                disabled={arrivingId === appointment.id}
                                onClick={() => void handleClearArrived(appointment)}
                              >
                                {t("studio.agenda.actions.arrived.undo")}
                              </Button>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );

  const renderQuickActionsInner = () => (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">
          {t("studio.dashboard.quickActions.title")}
        </h5>
      </CardHeader>
      <CardBody className="d-grid gap-2">
        <Button tag={Link} to="/agenda" color="primary">
          <i className="ri-calendar-check-line me-1" />
          {t("studio.dashboard.quickActions.agenda")}
        </Button>
        <Button tag={Link} to="/clients" color="soft-primary">
          <i className="ri-user-heart-line me-1" />
          {t("studio.dashboard.quickActions.clients")}
        </Button>
        {!isSdr ? (
          <Button tag={Link} to="/orders" color="soft-warning">
            <i className="ri-file-list-3-line me-1" />
            {t("studio.dashboard.quickActions.orders")}
          </Button>
        ) : null}
      </CardBody>
    </Card>
  );

  const renderImprovementsInner = () => (
    <Card>
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">
          {t("studio.dashboard.improvements.title")}
        </h5>
        <Link
          to="/support/tickets?category=product_feedback"
          className="btn btn-soft-primary btn-sm"
        >
          {t("studio.dashboard.improvements.viewAll")}
        </Link>
      </CardHeader>
      <CardBody>
        <p className="text-muted small">
          {t("studio.dashboard.improvements.intro")}
        </p>
        {recentImprovements.length === 0 ? (
          <p className="text-muted mb-0">
            {t("studio.dashboard.improvements.empty")}
          </p>
        ) : (
          <ul className="list-unstyled mb-0">
            {recentImprovements.map((item) => (
              <li key={item.id} className="mb-2">
                <Link to={`/support/tickets/${item.id}`} className="fw-medium">
                  #{item.public_number} — {item.subject}
                </Link>
                {item.resolved_at ? (
                  <div className="text-muted small">
                    {new Date(item.resolved_at).toLocaleDateString()}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );

  const renderContent = () => {
    const nodes: React.ReactNode[] = [];
    let index = 0;

    if (showKpiEmpty && allowCustomize) {
      nodes.push(
        <Card key="kpi-empty" className="border-0 shadow-sm mb-3">
          <CardBody className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-3">
            <div>
              <h6 className="mb-1">{t("studio.dashboard.customize.kpiEmptyTitle")}</h6>
              <p className="text-muted small mb-0">
                {t("studio.dashboard.customize.kpiEmptyBody")}
              </p>
            </div>
            <Button
              color="soft-primary"
              size="sm"
              type="button"
              onClick={() => setCustomizeOpen(true)}
            >
              {t("studio.dashboard.customize.open")}
            </Button>
          </CardBody>
        </Card>
      );
    }

    while (index < renderGroups.length) {
      const group = renderGroups[index];

      if (group.type === "kpi_row") {
        nodes.push(
          <Row key={`kpi-row-${index}`}>
            {group.ids.map((id) => renderKpiCard(id))}
          </Row>
        );
        index += 1;
        continue;
      }

      if (group.type === "day_row") {
        if (loading) {
          nodes.push(
            <div className="text-center py-5" key="dashboard-loading">
              <Spinner color="primary" />
              <p className="text-muted mt-3 mb-0">{t("studio.dashboard.loading")}</p>
            </div>
          );
          return nodes;
        }
        nodes.push(
          <Row className="g-3 mb-3" key={`day-row-${index}`}>
            {group.ids.map((id) => (
              <Col xl={id === "day_estimate" ? 4 : 8} md={12} key={id}>
                {id === "day_estimate" ? (
                  <DashboardDayEstimateCard
                    estimatedCents={estimatedCents}
                    paidCents={paidTodayCents}
                  />
                ) : (
                  <DashboardPendingsCard
                    items={pendingItems}
                    onOpenHealthRecord={(appointmentId) => {
                      const appointment = appointments.find(
                        (row) => row.id === appointmentId
                      );
                      if (appointment) {
                        openHealthRecord(appointment);
                      }
                    }}
                  />
                )}
              </Col>
            ))}
          </Row>
        );
        index += 1;
        continue;
      }

      if (group.type === "block" && group.id === "insights") {
        nodes.push(
          <React.Fragment key="insights">
            <DashboardInsightsPanel />
          </React.Fragment>
        );
        index += 1;
        continue;
      }

      if (loading) {
        nodes.push(
          <div className="text-center py-5" key="dashboard-loading">
            <Spinner color="primary" />
            <p className="text-muted mt-3 mb-0">{t("studio.dashboard.loading")}</p>
          </div>
        );
        return nodes;
      }

      if (group.type === "block" && group.id === "appointments_today") {
        nodes.push(
          <Row className="mb-3" key={`appointments-full-${index}`}>
            {renderAppointmentsCard()}
          </Row>
        );
        index += 1;
        continue;
      }

      if (group.type === "block" && group.id === "quick_actions") {
        nodes.push(
          <Row className="mt-1" key={`qa-${index}`}>
            <Col xl={12}>{renderQuickActionsInner()}</Col>
          </Row>
        );
        index += 1;
        continue;
      }

      if (group.type === "block" && group.id === "recent_improvements") {
        nodes.push(
          <Row className="mt-1" key={`ri-${index}`}>
            <Col xl={12}>{renderImprovementsInner()}</Col>
          </Row>
        );
        index += 1;
      }
    }

    return nodes;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <DashboardHomeHeader todayIso={today} />

        {isOwner ? (
          <FirstExperienceModal onHoldAutoPrompts={setHoldAutoPrompts} />
        ) : null}
        {isOwner ? <ConciergeDeepLinkHost /> : null}
        {holdAutoPrompts ? null : <WelcomePlayStoreBanner />}
        {isOwner && !holdAutoPrompts ? <ActivationHintBanner /> : null}
        {isOwner ? <TrialExpiryBanner /> : null}
        <DayCloseReminderBanner />

        {!layoutReady ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-3 mb-0">{t("studio.dashboard.loading")}</p>
          </div>
        ) : (
          <>
            {renderContent()}
            {allowCustomize ? (
              <div className="d-flex justify-content-center pt-4 pb-2 mt-2">
                <Button
                  color="light"
                  outline
                  size="sm"
                  type="button"
                  className="border text-muted"
                  onClick={() => setCustomizeOpen(true)}
                >
                  <i className="ri-layout-grid-line me-1" aria-hidden="true" />
                  {t("studio.dashboard.customize.open")}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Container>

      <ClientAnamnesisOffcanvas
        isOpen={anamnesisTarget !== null}
        onClose={() => setAnamnesisTarget(null)}
        clientId={anamnesisTarget?.id ?? 0}
        clientName={anamnesisTarget?.name ?? ""}
        serviceId={anamnesisTarget?.serviceId ?? null}
        initialTemplateId={anamnesisTarget?.initialTemplateId ?? null}
        onRecordSaved={handleAnamnesisRecordSaved}
      />

      {allowCustomize ? (
        <DashboardCustomizeOffcanvas
          isOpen={customizeOpen}
          onClose={() => {
            setPreviewLayout(null);
            setCustomizeOpen(false);
          }}
          widgets={committedLayout}
          onPreviewChange={setPreviewLayout}
          onSaved={(saved) => {
            setCommittedLayout(mergeLayoutPreference(saved, roles));
            setPreviewLayout(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default StudioDashboard;
